"use client";

import * as React from "react";
import { X } from "lucide-react";
import { cn } from "@/utils/utils";
import { styleDrivers } from "@/utils/componentStyle";

// ============================================
// TYPES
// ============================================
export type PillsVariant = "default" | "outline";
export type PillsSize = "sm" | "default" | "lg";

export interface PillsProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
  /** Controlled value */
  value?: string[];
  /** Uncontrolled default value */
  defaultValue?: string[];
  /** Called when values change */
  onChange?: (values: string[]) => void;
  /** Called when a value is added */
  onAdd?: (value: string) => void;
  /** Called when a value is removed */
  onRemove?: (value: string) => void;
  /** Placeholder text for input */
  placeholder?: string;
  /** Maximum number of tags allowed */
  maxTags?: number;
  /** Allow duplicate values */
  allowDuplicates?: boolean;
  /** Visual variant */
  variant?: PillsVariant;
  /** Size preset */
  size?: PillsSize;
  /** Disabled state */
  isDisabled?: boolean;
  /** Read-only state */
  isReadOnly?: boolean;
}

// ============================================
// COMPONENT
// ============================================
const Pills = React.forwardRef<HTMLDivElement, PillsProps>(
  (
    {
      className,
      value,
      defaultValue = [],
      onChange,
      onAdd,
      onRemove,
      placeholder = "Add tag...",
      maxTags,
      allowDuplicates = false,
      variant = "default",
      size = "default",
      isDisabled = false,
      isReadOnly = false,
      ...props
    },
    ref
  ) => {
    const [internalValue, setInternalValue] = React.useState<string[]>(defaultValue);
    const [inputValue, setInputValue] = React.useState("");
    const inputRef = React.useRef<HTMLInputElement>(null);

    const isControlled = value !== undefined;
    const tags = isControlled ? value : internalValue;

    const updateTags = (newTags: string[]) => {
      if (!isControlled) {
        setInternalValue(newTags);
      }
      onChange?.(newTags);
    };

    const addTag = (tag: string) => {
      const trimmed = tag.trim();
      if (!trimmed) return;
      if (!allowDuplicates && tags.includes(trimmed)) return;
      if (maxTags && tags.length >= maxTags) return;

      const newTags = [...tags, trimmed];
      updateTags(newTags);
      onAdd?.(trimmed);
      setInputValue("");
    };

    const removeTag = (tag: string) => {
      const newTags = tags.filter((t) => t !== tag);
      updateTags(newTags);
      onRemove?.(tag);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && inputValue) {
        e.preventDefault();
        addTag(inputValue);
      } else if (e.key === "Backspace" && !inputValue && tags.length > 0) {
        removeTag(tags[tags.length - 1]);
      }
    };

    const handleContainerClick = () => {
      if (!isDisabled && !isReadOnly) {
        inputRef.current?.focus();
      }
    };

    const sizeClasses = {
      sm: "min-h-8 text-caption",
      default: "min-h-9 text-body",
      lg: "min-h-10 text-body",
    };

    return (
      <div
        ref={ref}
        data-slot="pills"
        data-disabled={isDisabled || undefined}
        data-readonly={isReadOnly || undefined}
        className={cn(styleDrivers.pills.root, className)}
        {...props}
      >
        <div
          className={cn(
            styleDrivers.pills.container,
            sizeClasses[size],
            isDisabled && "cursor-not-allowed opacity-50",
            isReadOnly && "cursor-default"
          )}
          onClick={handleContainerClick}
        >
          {tags.map((tag) => (
            <span
              key={tag}
              className={cn(
                styleDrivers.pills.tag,
                variant === "outline" && "bg-transparent border border-border"
              )}
            >
              {tag}
              {!isDisabled && !isReadOnly && (
                <button
                  type="button"
                  className={styleDrivers.pills.tagRemove}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeTag(tag);
                  }}
                  aria-label={`Remove ${tag}`}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
          {!isReadOnly && (
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={tags.length === 0 ? placeholder : ""}
              disabled={isDisabled}
              className={styleDrivers.pills.input}
              aria-label="Add new tag"
            />
          )}
        </div>
      </div>
    );
  }
);

Pills.displayName = "Pills";

// ============================================
// EXPORTS
// ============================================
export { Pills };
